import metadata from './layerSemanticMetadata.json';

export interface LayerSemanticEntry {
  layer_name: string;
  service_type: string;
  layer_url: string;
  aliases: string[];
}

interface SemanticMetadata {
  layer_semantic_metadata: LayerSemanticEntry[];
}

export interface AliasMatch {
  entry: LayerSemanticEntry;
  /** The actual alias string from the metadata that hit. */
  matchedAlias: string;
}

const METADATA = metadata as SemanticMetadata;

/**
 * Normalize a GIS service URL for cross-source comparison: lowercase and
 * strip a trailing slash. Layer URLs in `layerSemanticMetadata.json` and the
 * URLs that come back from `selectGISLayers` can differ in case (the JSON
 * sometimes uses `ArcGIS`, the redux side typically lowercases) and in
 * trailing slash.
 */
export function normalizeServiceUrl(url: string | undefined | null): string {
  if (!url) return '';
  return url.trim().toLowerCase().replace(/\/+$/, '');
}

/**
 * Resolve a GIS service URL back to its semantic-metadata entry, if one
 * exists. Lets AI- and alias-driven matches on the same layer surface the
 * same friendly display name.
 */
export function findLayerSemanticEntry(
  serviceUrl: string | undefined | null,
  entries: LayerSemanticEntry[] = METADATA.layer_semantic_metadata,
): LayerSemanticEntry | null {
  const target = normalizeServiceUrl(serviceUrl);
  if (!target) return null;
  return entries.find(e => normalizeServiceUrl(e.layer_url) === target) ?? null;
}

// --- Fuzzy / partial matching ---

/**
 * Tokens shorter than this are required to match exactly — partial / fuzzy
 * comparisons on 1-2 character tokens generate too many false positives
 * (e.g. "in", "a", "of" landing on every English query).
 */
const MIN_FUZZY_LEN = 3;

/**
 * Minimum (shorter / longer) length ratio required for a prefix or
 * substring match between two tokens. Without this guard a 3-char alias
 * like "bus" would prefix-match "business" and erroneously activate the
 * layer for unrelated queries.
 */
const MIN_LENGTH_RATIO = 0.6;

/**
 * Per-token similarity threshold below which we treat the tokens as
 * non-matching. Final per-alias scores are averaged across tokens, then
 * compared against this same threshold to filter out weak hits.
 */
const SCORE_THRESHOLD = 0.6;

function tokenize(s: string): string[] {
  return s.toLowerCase().match(/[a-z0-9]+/g) ?? [];
}

/**
 * Standard iterative two-row Levenshtein. Inlined to avoid a dependency for
 * what is at most a few-dozen-character comparison.
 */
function levenshtein(a: string, b: string): number {
  if (a === b) return 0;
  if (!a.length) return b.length;
  if (!b.length) return a.length;
  const prev: number[] = new Array(b.length + 1);
  const curr: number[] = new Array(b.length + 1);
  for (let j = 0; j <= b.length; j++) prev[j] = j;
  for (let i = 1; i <= a.length; i++) {
    curr[0] = i;
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      curr[j] = Math.min(curr[j - 1] + 1, prev[j] + 1, prev[j - 1] + cost);
    }
    for (let j = 0; j <= b.length; j++) prev[j] = curr[j];
  }
  return prev[b.length];
}

/**
 * Score how similar two individual tokens are on a 0..1 scale.
 *
 * Match tiers, in order of preference:
 *   1. Exact equality.
 *   2. One token is a prefix of the other (catches typos like "wildfir"
 *      and pluralization like "wildfires" against "wildfire").
 *   3. One token contains the other (handles compounds like "schoolbus").
 *   4. Levenshtein distance within ~25% of the longer token (catches small
 *      typos like "polic" / "polise" against "police").
 *
 * The length-ratio guard in tiers 2-3 prevents pathological matches such
 * as "bus" prefix-matching "business".
 */
function tokenSimilarity(a: string, b: string): number {
  if (a === b) return 1;

  const minLen = Math.min(a.length, b.length);
  const maxLen = Math.max(a.length, b.length);
  if (minLen < MIN_FUZZY_LEN) return 0;

  const ratio = minLen / maxLen;

  if (a.startsWith(b) || b.startsWith(a)) {
    if (ratio >= MIN_LENGTH_RATIO) return 0.7 + 0.3 * ratio;
  }

  if (a.includes(b) || b.includes(a)) {
    if (ratio >= MIN_LENGTH_RATIO) return 0.55 + 0.25 * ratio;
  }

  const dist = levenshtein(a, b);
  const allowed = Math.floor(maxLen / 4);
  if (dist > 0 && dist <= allowed) {
    return Math.max(0, 1 - dist / maxLen) * 0.9;
  }

  return 0;
}

/**
 * Score how strongly an alias matches a query.
 *
 * Each alias token must find at least one query token that scores above
 * `SCORE_THRESHOLD`; if any alias token has no qualifying counterpart the
 * alias is considered a non-match (returns 0).
 *
 * The returned score is the sum of per-token similarities weighted by the
 * matched alias-token's character length. This naturally prefers longer,
 * more-specific aliases over short generic ones — `(maxLen * 100 + count)`
 * scoring from the prior exact-match implementation, generalized.
 */
function aliasScore(alias: string, query: string): number {
  const aliasToks = tokenize(alias);
  const queryToks = tokenize(query);
  if (aliasToks.length === 0 || queryToks.length === 0) return 0;

  let weightedSum = 0;
  let weightTotal = 0;
  for (const at of aliasToks) {
    let bestForToken = 0;
    for (const qt of queryToks) {
      const s = tokenSimilarity(at, qt);
      if (s > bestForToken) bestForToken = s;
    }
    if (bestForToken < SCORE_THRESHOLD) return 0;
    weightedSum += bestForToken * at.length;
    weightTotal += at.length;
  }

  const avg = weightTotal > 0 ? weightedSum / weightTotal : 0;
  if (avg < SCORE_THRESHOLD) return 0;
  return weightedSum;
}
const LAYER_NAME_BOOST = 1.25;

export function matchLayerAlias(
  query: string,
  entries: LayerSemanticEntry[] = METADATA.layer_semantic_metadata,
): AliasMatch | null {
  const lowerQuery = query.trim().toLowerCase();
  if (!lowerQuery) return null;

  let best: { entry: LayerSemanticEntry; alias: string; score: number } | null =
    null;

  for (const entry of entries) {
    let bestAlias = '';
    let bestCandidateScore = 0;
    let hits = 0;

    // Boost direct name hits so they aren't drowned out by generic alias
    // overlap on a different entry.
    if (entry.layer_name) {
      const nameScore = aliasScore(entry.layer_name, query) * LAYER_NAME_BOOST;
      if (nameScore > 0) {
        hits += 1;
        bestCandidateScore = nameScore;
        bestAlias = entry.layer_name;
      }
    }

    for (const alias of entry.aliases) {
      const s = aliasScore(alias, query);
      if (s <= 0) continue;
      hits += 1;
      if (s > bestCandidateScore) {
        bestCandidateScore = s;
        bestAlias = alias;
      }
    }
    if (hits === 0) continue;
    const entryScore = bestCandidateScore + Math.log2(hits + 1);
    if (!best || entryScore > best.score) {
      best = { entry, alias: bestAlias, score: entryScore };
    }
  }

  if (!best) return null;
  return { entry: best.entry, matchedAlias: best.alias };
}
